import React, { useState, useRef, useEffect, KeyboardEvent } from "react";
import cn from "classnames";
import styles from "./dashboard-card.module.scss";
import { Dashboard } from "@src/types/dashboards";
import { DashboardCardPopover } from "@src/components/atoms/dashboard-card-popover/dashboard-card-popover";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import ellipsisIcon from "@src/assets/svg/ellipsis-horizontal-circle.svg";
import dashboardIcon from "@src/assets/svg/dashboard-icon-blue.svg";
import dashboardIconGray from "@src/assets/svg/dashboard-icon-gray.svg";
import bookmarkIcon from "@src/assets/svg/bookmark-blue.svg";
import bookmarkFilledIcon from "@src/assets/svg/bookmark-filled-blue.svg";
import saveIcon from "@src/assets/svg/save-right-blue-icon.svg";
import cancelIcon from "@src/assets/svg/close-orange-icon.svg";
import { useI18N } from "@src/language/language-context";

interface DashboardCardProps {
  dashboard: Dashboard;
  isSelected: boolean;
  isDefault: boolean;
  isCollapsed: boolean;
  isCreating?: boolean;
  isRenaming?: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, name: string, description: string | null) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string) => void;
  onCancelCreate?: () => void;
  onConfirmCreate?: (name: string, description: string | null) => void;
}

const cssClassName = "dashboard-card";

export const DashboardCard = ({
  dashboard,
  isSelected,
  isDefault,
  isCollapsed,
  isCreating = false,
  isRenaming: isRenamingProp = false,
  onSelect,
  onRename,
  onDuplicate,
  onDelete,
  onSetDefault,
  onCancelCreate,
  onConfirmCreate,
}: DashboardCardProps) => {
  const i18n = useI18N();
  const [isRenaming, setIsRenaming] = useState(isRenamingProp);
  const [editName, setEditName] = useState(dashboard.name);
  const [editDescription, setEditDescription] = useState(
    dashboard.description ?? "",
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isDraft = dashboard.status === "draft";
  const isEditing = isCreating || isRenaming;

  const texts = {
    draft: i18n("modules.dashboardCard.draft"),
    published: i18n("modules.dashboardCard.published"),
    setDefault: i18n("modules.dashboardCard.setDefault"),
    namePlaceholder: i18n("modules.dashboardCard.namePlaceholder"),
    descriptionPlaceholder: i18n(
      "modules.dashboardCard.descriptionPlaceholder",
    ),
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!isEditing) {
      onSelect(dashboard.id);
    }
  };

  const handleConfirmRename = () => {
    if (editName.trim()) {
      onRename(dashboard.id, editName.trim(), editDescription.trim() || null);
      setIsRenaming(false);
    }
  };

  const handleCancelRename = () => {
    setIsRenaming(false);
    setEditName(dashboard.name);
  };

  const handleConfirmCreate = () => {
    if (editName.trim() && onConfirmCreate) {
      onConfirmCreate(editName.trim(), editDescription.trim() || null);
    }
  };

  const handleCancelCreate = () => {
    onCancelCreate?.();
  };

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      isCreating ? handleConfirmCreate() : handleConfirmRename();
    } else if (e.key === "Escape") {
      isCreating ? handleCancelCreate() : handleCancelRename();
    }
  };

  const handleDescriptionKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter inserts a newline (default); only Escape aborts.
    if (e.key === "Escape") {
      isCreating ? handleCancelCreate() : handleCancelRename();
    }
  };

  const handleMenuToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMenuOpen((prev) => !prev);
  };

  const handleSetDefaultClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    onSetDefault(dashboard.id);
  };

  if (isCollapsed) {
    return (
      <div
        className={cn(styles[`${cssClassName}--collapsed`], {
          [styles[`${cssClassName}--collapsed--selected`]]: isSelected,
        })}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        role="button"
        tabIndex={0}
        title={dashboard.name}
      >
        <div className={styles[`${cssClassName}--collapsed__icon-tile`]}>
          <img
            className={styles[`${cssClassName}--collapsed__icon`]}
            src={isSelected ? dashboardIcon : dashboardIconGray}
            alt=""
          />
        </div>
        <span className={styles[`${cssClassName}--collapsed__label`]}>
          {dashboard.name}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(styles[cssClassName], {
        [styles[`${cssClassName}--selected`]]: isSelected,
        [styles[`${cssClassName}--editing`]]: isEditing,
      })}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (
          e.target === e.currentTarget &&
          !isEditing &&
          (e.key === "Enter" || e.key === " ")
        ) {
          e.preventDefault();
          handleClick();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className={styles[`${cssClassName}__icon-tile`]}>
        <img
          className={styles[`${cssClassName}__icon`]}
          src={isSelected ? dashboardIcon : dashboardIconGray}
          alt=""
        />
      </div>

      <div className={styles[`${cssClassName}__main`]}>
        {isEditing ? (
          <>
            <input
              ref={inputRef}
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleNameKeyDown}
              className={styles[`${cssClassName}__input`]}
              placeholder={texts.namePlaceholder}
              onClick={(e) => e.stopPropagation()}
            />
            {isCreating || isRenaming ? (
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                onKeyDown={handleDescriptionKeyDown}
                className={styles[`${cssClassName}__textarea`]}
                placeholder={texts.descriptionPlaceholder}
                onClick={(e) => e.stopPropagation()}
                rows={2}
              />
            ) : null}
          </>
        ) : (
          <>
            <h3 className={styles[`${cssClassName}__title`]}>
              {dashboard.name}
            </h3>
            {dashboard.description && (
              <p className={styles[`${cssClassName}__description`]}>
                {dashboard.description}
              </p>
            )}
          </>
        )}
      </div>

      {isEditing ? (
        <div className={styles[`${cssClassName}__edit-actions`]}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              isCreating ? handleConfirmCreate() : handleConfirmRename();
            }}
            className={styles[`${cssClassName}__edit-button`]}
          >
            <img src={saveIcon} alt="Save" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              isCreating ? handleCancelCreate() : handleCancelRename();
            }}
            className={styles[`${cssClassName}__edit-button`]}
          >
            <img src={cancelIcon} alt="Cancel" />
          </button>
        </div>
      ) : (
        <div className={styles[`${cssClassName}__actions`]}>
          <span
            className={cn(styles[`${cssClassName}__status-badge`], {
              [styles[`${cssClassName}__status-badge--draft`]]: isDraft,
              [styles[`${cssClassName}__status-badge--published`]]: !isDraft,
            })}
          >
            {isDraft ? texts.draft : texts.published}
          </span>
          {/* Set as default is hidden for drafts (doc §2.5.1) */}
          {!isDraft && (
            <IconButton
              fun={handleSetDefaultClick}
              icon={isDefault ? bookmarkFilledIcon : bookmarkIcon}
              tooltip={texts.setDefault}
              tooltipPosition="left"
            />
          )}
          <div className={styles[`${cssClassName}__menu`]}>
            <button
              className={styles[`${cssClassName}__menu-button`]}
              onClick={handleMenuToggle}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="Dashboard actions"
            >
              <img src={ellipsisIcon} alt="" />
            </button>
            {isMenuOpen && (
              <DashboardCardPopover
                onRename={() => {
                  setEditName(dashboard.name);
                  setEditDescription(dashboard.description ?? "");
                  setIsRenaming(true);
                }}
                onDuplicate={() => onDuplicate(dashboard.id)}
                onDelete={() => onDelete(dashboard.id)}
                setPopoverIsOpen={setIsMenuOpen}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
